import { useMemo } from "react";
import { useIntl } from "@leagueplatform/locales";
import { useForm } from "@leagueplatform/web-common";
import { StackLayout, Box } from "@leagueplatform/genesis-core";
import { FormContainer } from "components/forms/form-container/form-container.component";
import { RHFTextInput } from "components/form-elements/rhf-text-input.component";
import {
  phoneNumberValidation,
  emailValidation,
} from "common/utils/rhf-form-validation.util";
import type { ProfileData } from "types/types";

export interface ContactInfoSectionProps {
  data: ProfileData | undefined;
  onSubmit: (values: ContactInfoFormValues) => Promise<void>;
}

export type ContactInfoFormValues = {
  primaryPhoneNumber: string;
  mobilePhoneNumber: string;
  email: string;
};

export const ContactInfoSection = ({
  data,
  onSubmit,
}: ContactInfoSectionProps) => {
  const { formatMessage } = useIntl();

  const defaultValues: ContactInfoFormValues = useMemo(
    () => ({
      primaryPhoneNumber: data?.primaryPhoneNumber || "",
      mobilePhoneNumber: data?.mobilePhoneNumber || "",
      email: data?.email || "",
    }),
    [data]
  );

  const formMethods = useForm<ContactInfoFormValues>({
    defaultValues,
  });

  const profileFieldValues = useMemo(() => {
    if (!data) return [];

    return [
      {
        label: formatMessage({ id: "STR_PRIMARY_PHONE_NUMBER" }),
        value: data.primaryPhoneNumber,
        isLocked: data.isContactInfoLocked,
      },
      {
        label: formatMessage({ id: "STR_MOBILE_PHONE_NUMBER" }),
        value: data.mobilePhoneNumber,
        isLocked: false,
      },
      {
        label: formatMessage({ id: "STR_EMAIL_ADDRESS" }),
        value: data.email,
        isLocked: false,
      },
    ];
  }, [data, formatMessage]);

  return (
    <FormContainer
      heading={formatMessage({ id: "STR_CONTACT_INFORMATION" })}
      profileFieldValues={profileFieldValues}
      formMethods={formMethods}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
    >
      <StackLayout spacing="$one" horizontalAlignment="stretch">
        {/* Row 1: Primary Phone + Mobile Phone */}
        <Box
          css={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "$one",
            "@mobile": { gridTemplateColumns: "1fr" },
          }}
        >
          <RHFTextInput
            id="primaryPhoneNumber"
            type="tel"
            label={formatMessage({ id: "STR_PRIMARY_PHONE_NUMBER" })}
            required
            registerOptions={phoneNumberValidation(
              formatMessage({ id: "STR_PHONE_VALIDATION_ERROR" })
            )}
          />
          <RHFTextInput
            required={false}
            id="mobilePhoneNumber"
            type="tel"
            label={formatMessage({ id: "STR_MOBILE_PHONE_NUMBER" })}
            registerOptions={phoneNumberValidation(
              formatMessage({ id: "STR_PHONE_VALIDATION_ERROR" })
            )}
          />
        </Box>
        {/* Row 2: Email (single column) */}
        <Box
          css={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "$one",
            "@mobile": { gridTemplateColumns: "1fr" },
          }}
        >
          <RHFTextInput
            id="email"
            type="email"
            label={formatMessage({ id: "STR_EMAIL_ADDRESS" })}
            required
            registerOptions={emailValidation(
              formatMessage({ id: "STR_EMAIL_VALIDATION_ERROR" })
            )}
          />
        </Box>
      </StackLayout>
    </FormContainer>
  );
};
